import { Flex, Tooltip, Typography } from "antd";
import { ColumnType } from "antd/es/table";
import {
  COMPANY_TYPES,
  contractCurrencyName,
  getEnumKeys,
  Network,
  REVERSE_ORDER_MAP,
  WALLETS_WHITELIST_NETWORK_FILTERS,
  WALLETS_WHITELIST_STATUS_COLORS,
  WALLETS_WHITELIST_STATUS_TEXT,
  WalletsWhitelistType,
} from "interfaces";
import { WalletsWhitelistStore } from "stores/WalletsWhitelistStore";
import { CustomButton, getCryptoIconRound, Status, TxButton } from "ui";
import {
  dateFormat,
  detectWalletNetwork,
  EXPLORER_LINK_TYPE,
  getBlockchainExplorerLink,
  getCompanyType,
  getFilterIcon,
  getSortIcon,
} from "utils";
import styles from "./walletsWhitelist.module.css";
import { UserStore } from "stores/UserStore";
import { ReactComponent as DotIcon } from "images/Steps/dot.svg";
import { WalletAddressActionBtn } from "./WalletAddressActionBtn";

export const getColumns = ({
  whiteList,
  user,
}: {
  whiteList: WalletsWhitelistStore;
  user: UserStore;
}): Array<ColumnType<WalletsWhitelistType>> => {
  const { isRiskManager, isLPRiskManager } = user.roles;

  return [
    isRiskManager
      ? {
          dataIndex: "companyName",
          key: "companyName",
          title: "Компания",
          width: 300,
          render: (_: any, record: WalletsWhitelistType) => {
            const companyType = getCompanyType(record.companyOgrn);
            const companyTypeTitle = companyType === COMPANY_TYPES.COMPANY ? "ОГРН:" : "ОГРНИП:";
            return (
              <Flex vertical gap={2}>
                <Tooltip trigger="hover" placement="topLeft" title={record.companyName}>
                  <span>{record.companyName}</span>
                </Tooltip>
                <Flex align="center" gap="small">
                  <Typography.Text className={`${styles.statusDateText}`}>ИНН: {record.companyInn}</Typography.Text>
                  <DotIcon className={`${styles.statusDateText}`} />
                  <Typography.Text className={`${styles.statusDateText}`}>
                    {companyTypeTitle} {record.companyOgrn}
                  </Typography.Text>
                </Flex>
              </Flex>
            );
          },
        }
      : null,
    {
      dataIndex: "destinationWallet",
      key: "destinationWallet",
      title: "Адрес кошелька",
      width: 180,
      render: (_: unknown, record: WalletsWhitelistType) => {
        const network = detectWalletNetwork(record.destinationWallet);
        return network ? (
          <TxButton
            text={record.destinationWallet}
            isWalletAddress
            showCopyBtn={false}
            href={getBlockchainExplorerLink(network, record.destinationWallet, EXPLORER_LINK_TYPE.address)}
          />
        ) : (
          "Некорректный адрес"
        );
      },
    },
    {
      dataIndex: "network",
      key: "network",
      title: "Сеть",
      filterIcon: (filtered: boolean) => getFilterIcon(filtered),
      filterMultiple: true,
      filteredValue: whiteList.queryParams["network"],
      filtered: "network" in whiteList.queryParams,
      filters: getEnumKeys(WALLETS_WHITELIST_NETWORK_FILTERS).map((network) => ({
        text: network,
        value: network,
      })),
      onFilter: (value: any, record: WalletsWhitelistType) => record.network === value,
      width: 100,
      render: (_: any, record: WalletsWhitelistType) => {
        return (
          <Flex align="center" gap="small">
            {getCryptoIconRound(record.network, "xl")}
            <Typography.Text>{contractCurrencyName[record.network]}</Typography.Text>
          </Flex>
        );
      },
    },

    {
      dataIndex: "usedCount",
      key: "used",
      title: "Кол-во использований",
      sorter: { multiple: 3 },
      sortOrder: whiteList.sorters?.usedCount ? REVERSE_ORDER_MAP[whiteList.sorters.usedCount] : undefined,
      sortIcon: getSortIcon,
      width: 100,
      align: "left",
      render: (_: any, record: WalletsWhitelistType) => {
        return (
          <Flex justify="start">
            <Typography.Text>{record.usedCount}</Typography.Text>
          </Flex>
        );
      },
    },
    {
      dataIndex: "createdAt",
      key: "createdAt",
      title: "Дата добавления",
      sorter: { multiple: 3 },
      sortOrder: whiteList.sorters?.createdAt ? REVERSE_ORDER_MAP[whiteList.sorters.createdAt] : undefined,
      sortIcon: getSortIcon,
      width: 200,
      render: (_: any, record: WalletsWhitelistType) => {
        return (
          <Flex vertical align="start">
            <Typography.Text>{dateFormat(record.createdAt, true)}</Typography.Text>
            <Typography.Text className="text-description text-xs">{record.createdByName}</Typography.Text>
          </Flex>
        );
      },
    },
    {
      dataIndex: "statusDate",
      key: "statusDate",
      title: "Дата изменения",
      width: 200,
      sorter: { multiple: 3 },
      sortOrder: whiteList.sorters?.statusDate ? REVERSE_ORDER_MAP[whiteList.sorters.statusDate] : undefined,
      sortIcon: getSortIcon,
      render: (_: any, record: WalletsWhitelistType) => {
        return <Typography.Text>{dateFormat(record.statusDate ?? "", true)}</Typography.Text>;
      },
    },
    {
      dataIndex: "status",
      key: "status",
      title: "Статус",
      width: 150,
      align: "right",
      render: (_: any, record: WalletsWhitelistType) => {
        return (
          <Flex vertical align="end" gap={4}>
            <Status
              color={WALLETS_WHITELIST_STATUS_COLORS[record.status]}
              children={WALLETS_WHITELIST_STATUS_TEXT[record.status]}
            />
          </Flex>
        );
      },
    },
    isLPRiskManager && {
      dataIndex: "actions",
      key: "actions",
      title: "Действия",
      align: "right",
      width: 100,
      render: (_: any, record: WalletsWhitelistType) => {
        return (
          <Flex justify="end">
            <WalletAddressActionBtn
              status={record.status}
              walletAddress={record.destinationWallet}
              walletId={record.id}
              network={record.network as Network}
            />
          </Flex>
        );
      },
    },
  ].filter((i) => !!i) as ColumnType<WalletsWhitelistType>[];
};
